import type { ExportFit, ExportPageOptions } from '@mss_creator/shared';
import { FIT_HINTS, FIT_LABELS } from './export-customization';
import { ExportSegmentedControl } from './export-segmented-control';
import type { SegmentOption } from './export-segmented-control';
import { ExportSelectField } from './export-select-field';

interface ExportPageControlsProps {
  page: ExportPageOptions;
  onChange: (page: ExportPageOptions) => void;
  /** Decision L5 advisory, computed by the page from the live tiling. */
  legibilityNotice: string | null;
}

const FORMAT_OPTIONS: readonly SegmentOption<ExportPageOptions['format']>[] = [
  { value: 'a4', label: 'A4' },
  { value: 'a3', label: 'A3' },
];

const ORIENTATION_OPTIONS: readonly SegmentOption<ExportPageOptions['orientation']>[] = [
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Paysage' },
];

const MARGIN_OPTIONS: readonly SegmentOption<ExportPageOptions['margins']>[] = [
  { value: 'minimales', label: 'Minimales' },
  { value: 'normales', label: 'Normales' },
  { value: 'larges', label: 'Larges' },
];

/** The wire's `fontSizePt` range (8-14), as select values. */
const FONT_SIZE_OPTIONS: readonly SegmentOption<string>[] = [8, 9, 10, 11, 12, 13, 14].map(
  (size) => ({ value: String(size), label: `${size} pt` }),
);

/** Display order for « Ajustement » — no reduction first, most aggressive last. */
const FIT_ORDER: readonly ExportFit[] = [
  'aucun',
  'une-page-de-large',
  'une-page-de-haut',
  'une-seule-page',
];
const FIT_OPTIONS: readonly SegmentOption<ExportFit>[] = FIT_ORDER.map((fit) => ({
  value: fit,
  label: FIT_LABELS[fit],
}));

/** The « Page » category (PDF only): paper, margins, base type size and fit. */
export function ExportPageControls({ page, onChange, legibilityNotice }: ExportPageControlsProps) {
  return (
    <>
      <ExportSegmentedControl
        label="Format"
        value={page.format}
        options={FORMAT_OPTIONS}
        onChange={(format) => onChange({ ...page, format })}
      />
      <ExportSegmentedControl
        label="Orientation"
        value={page.orientation}
        options={ORIENTATION_OPTIONS}
        onChange={(orientation) => onChange({ ...page, orientation })}
      />
      <ExportSegmentedControl
        label="Marges"
        value={page.margins}
        options={MARGIN_OPTIONS}
        onChange={(margins) => onChange({ ...page, margins })}
      />
      <ExportSelectField
        label="Police"
        value={String(page.fontSizePt)}
        options={FONT_SIZE_OPTIONS}
        onChange={(size) => onChange({ ...page, fontSizePt: Number(size) })}
      />
      <div className="space-y-1">
        <ExportSelectField
          label="Ajustement"
          value={page.fit}
          options={FIT_OPTIONS}
          onChange={(fit) => onChange({ ...page, fit })}
        />
        <p className="text-caption text-gray-500">{FIT_HINTS[page.fit]}</p>
      </div>
      {/* Decision L5 — advisory only: the chosen fit is always honoured. */}
      {legibilityNotice !== null && (
        <output className="block rounded-md border border-warning bg-jaune-tint px-3 py-2 text-caption">
          {legibilityNotice}
        </output>
      )}
    </>
  );
}
